<script>

    import {onMount, getContext, createEventDispatcher} from "svelte";
    import {get} from "svelte/store";
    import {API, to} from "@API"
    import BeltSize from "@assets/Belt-Size@2x.png"
    import Billing from "./Billing.svelte";
    import ConfirmationModal from "./ConfirmationModal.svelte";
    import { fade } from 'svelte/transition';
    import {forcesLocalPickup} from '@utils/fulfillment.js';

    const BillingService = API.service(`${process.env.API}/users/:guid/billing/`, false)
    BillingService.external(true);

    const VIDEO_URL = 'https://cdn.oto.coach/static-content/oto/chest_strap_onboarding.mp4';

    export let context = "SHOP";
    export let showBilling = false;

    let shippingError, shippingResponse;
    /** Missing-field errors specific to the address, forwarded down into PlaceAutocomplete. */
    let addressError = [];
    /** Set once the user tries to continue, so the warning banner can then keep itself in sync as fields get fixed. */
    let hasAttemptedSubmit = false;

    let {user, org, shopData, location, pick_up_location} = getContext(context);

    export let settings = {
      productType: 'single',
      pick_up_location: getContext(context).pick_up_location
    }

    let loading = false;
    let dispatch = createEventDispatcher();
    let equipment = 3;
    let equipment2 = 3;
    let mediaTab = 'chart';
    let billingAddress = {};
    let missingFields = [];
    /** Address already saved on the account, shown instead of an empty form */
    let addressOnFile = null;

    let showWarning = false;
    let modal;
    let zoomChart = false;

    let measurement = '';
    let measurementUnit = 'in';

    // Ranges are front-of-chest measurements in inches.
    let options = [
        {label: "0", value: 'sensor-kit-s0', max: 16.5, rangeLabel: '< 16.5 in'},
        {label: "1", value: 'sensor-kit-s1', min: 16.5, max: 17.6, rangeLabel: '16.5–17.6 in'},
        {label: "2", value: 'sensor-kit-s2', min: 17.6, max: 18.6, rangeLabel: '17.6–18.6 in'},
        {label: "3", value: 'sensor-kit-s3', min: 18.6, max: 19.7, rangeLabel: '18.6–19.7 in'},
        {label: "4", value: 'sensor-kit-s4', min: 19.7, max: 21.1, rangeLabel: '19.7–21.1 in'},
        {label: "5", value: 'sensor-kit-s5', min: 21.1, rangeLabel: '21.1–22.7 in'},
    ];

    $: selectedOption = options[equipment - 1];
    $: needsShipping = showBilling && settings.pick_up_location === 'Mail to me';

    onMount(async () => {
        let u = user ? (user.subscribe ? get(user) : user) : null;
        if (!u) return;

        const [error, response] = await to(BillingService.get({guid: u.guid}));
        // Nothing on file, or we could not read it — collect the address here so a
        // mailed kit always has somewhere to go.
        if (error || !response || !response.guid) {
            showBilling = true;
            return;
        }

        addressOnFile = response;
        billingAddress = {
            street: response.street,
            city: response.city,
            state: response.state,
            country: response.country,
            postal_code: response.postal_code,
            // The address record carries no phone, so the account's number stands in.
            phonenumber: u.phone || '',
        };
    });

    /** Swaps the saved address for a blank form, keeping the phone we already know. */
    function useDifferentAddress() {
        billingAddress = {phonenumber: billingAddress.phonenumber || ''};
        addressOnFile = null;
        showBilling = true;
    }

    function selectSize(index) {
        equipment = index + 1;
    }

    function selectSecondSize(index) {
        equipment2 = index + 1;
    }

    function applyMeasurement() {
        const raw = parseFloat(measurement);
        if (isNaN(raw)) return;

        const inches = measurementUnit === 'mm' ? raw / 25.4 : raw;
        const match = options.findIndex(o => {
            const aboveMin = o.min === undefined || inches >= o.min;
            const belowMax = o.max === undefined || inches < o.max;
            return aboveMin && belowMax;
        });
        equipment = (match === -1 ? options.length : match + 1);
    }

    function setUnit(unit) {
        measurementUnit = unit;
        applyMeasurement();
    }

    function missingAddressFields(details) {
        const fields = ["street", "city", "country", "postal_code", "phonenumber"];
        return fields.filter(f => {
          if (f === 'postal_code') return !details[f] && !details['zip'];
          return !details[f];
        });
    }

    /** Verification that also surfaces the warning — only for the submit path. */
    function basicAddressVerification(details) {
        hasAttemptedSubmit = true;
        missingFields = missingAddressFields(details);
        // phonenumber isn't part of PlaceAutocomplete's fields, so keep it out of
        // the error list that drops the user into the manual address editor.
        addressError = missingFields.filter(f => f !== 'phonenumber');
        return missingFields.length === 0;
    }

    // Once a submit attempt has surfaced the warning banner, keep it (and the
    // PlaceAutocomplete error list) in sync as the user fixes fields — e.g. by
    // hitting "Save address" in the manual editor, or filling in the phone number.
    $: if (hasAttemptedSubmit) {
        missingFields = missingAddressFields(billingAddress);
        addressError = missingFields.filter(f => f !== 'phonenumber');
    }

    async function onSendSize() {
        loading = true;
        modal.state('waiting');
        missingFields = [];
       if (needsShipping) {
         let u = user ? (user.subscribe ? get(user) : user) : user;
         if(basicAddressVerification(billingAddress)) {
          if (billingAddress.zip) {
            billingAddress.postal_code = billingAddress.zip;
          }
            // save address
            [shippingError, shippingResponse] = await to(BillingService.post({
                  guid:u.guid,
                  ...billingAddress
                })
            );
            if (shippingResponse) {
              dispatchComplete();
            } else{
              alert("Error saving billing information, please contact support");
              modal.state('idle');
              return
            }
          }else{
            loading = false;
            handleCloseWarning();
          }
        }else{
          dispatchComplete()
        }

    }

    function dispatchComplete(){
      let belts = [options[equipment - 1].value];
      if (settings.productType === 'couple') {
          belts.push(options[equipment2 - 1].value);
      }
      const dispatchPayload = {belts};
      // A saved address needs no warning here, so this check stays silent.
      if (settings.pick_up_location === 'Mail to me' && missingAddressFields(billingAddress).length === 0) {
          dispatchPayload.billingAddress = billingAddress;
      }
      dispatch('beltSize', dispatchPayload);
      handleCloseWarning();

    }

    function handleCloseWarning() {
        modal.state('idle')
        showWarning = false;
    }

</script>

<style lang="scss">
  .panel {
    @apply rounded-xl border border-gray-200 bg-white;
  }

  .tab-bar {
    @apply flex items-center gap-2 p-2 border-b border-gray-100;
  }

  .tab {
    @apply flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold text-gray-500 hover:bg-gray-50;

    &.active {
      @apply bg-org-secondary/10 text-org-secondary;
    }
  }

  .media-wrapper {
    @apply relative w-full overflow-hidden bg-gray-50;
    aspect-ratio: 16/9;
  }

  .media-wrapper img,
  .media-wrapper video {
    @apply absolute inset-0 h-full w-full object-contain;
  }

  .hint {
    @apply flex items-start gap-2 text-sm text-gray-600;
  }

  .hint-index {
    @apply flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-org-secondary/10 text-xs font-bold text-org-secondary;
  }

  .size-grid {
    @apply grid grid-cols-3 gap-3 sm:grid-cols-6;
  }

  .size-tile {
    @apply rounded-xl border border-gray-200 bg-white py-3 text-center transition hover:border-gray-300;

    &.selected {
      @apply border-org-secondary ring-1 ring-org-secondary bg-org-secondary/5;
    }
  }

  .size-tile-label {
    @apply block text-2xl font-bold text-gray-900;
  }

  .size-tile-range {
    @apply block text-[11px] text-gray-500;
  }

  .option-card {
    @apply flex flex-1 items-start gap-3 rounded-xl border border-gray-200 bg-white p-4 text-left cursor-pointer transition hover:border-gray-300;

    &.selected {
      @apply border-org-secondary ring-1 ring-org-secondary bg-org-secondary/5;
    }
  }

  .unit-toggle {
    @apply flex overflow-hidden rounded-lg border border-gray-200;
  }

  .unit-button {
    @apply px-3 py-2 text-sm font-semibold text-gray-500 hover:bg-gray-50;

    &.active {
      @apply bg-org-secondary text-over-secondary;
    }
  }

  .action-button {
    @apply mt-6 w-full rounded-lg bg-org-secondary py-3 font-semibold text-over-secondary hover:opacity-95 disabled:opacity-50;
    box-shadow: 0 12px 20px -12px var(--secondary-color);
  }

  .warning {
    @apply mt-4 flex items-center gap-3 rounded-xl bg-red-50 p-3 text-sm text-red-500;
  }

  .section-label {
    @apply flex items-baseline justify-between gap-3;
  }
</style>

<div class="belt-chooser text-left">
  <div class="panel overflow-hidden">
    <div class="tab-bar">
      <button type="button" class="tab" class:active={mediaTab === 'video'} on:click={() => (mediaTab = 'video')}>
        <i class="fa-solid fa-circle-play"></i> How to measure
      </button>
      <button type="button" class="tab" class:active={mediaTab === 'chart'} on:click={() => (mediaTab = 'chart')}>
        <i class="fa-regular fa-table-cells"></i> Sizing chart
      </button>
      <span class="ml-auto pr-2 text-xs text-gray-400">0:30</span>
    </div>

    <div class="media-wrapper">
      {#if mediaTab === 'chart'}
        <img src="{BeltSize}" alt="size chart" in:fade={{duration: 150}}>
        <button
          class="absolute top-2 right-2 z-10 inline-flex items-center justify-center rounded-full bg-white/90 px-3 py-2 text-black shadow hover:bg-white"
          on:click={() => (zoomChart = true)}
          aria-label="Zoom sizing chart"
        >
          <i class="fa-solid fa-magnifying-glass-plus"></i>
        </button>
      {:else}
        <video controls src="{VIDEO_URL}" in:fade={{duration: 150}}></video>
      {/if}
    </div>
  </div>

  <div class="mt-4 grid gap-3 sm:grid-cols-3">
    <p class="hint"><span class="hint-index">1</span> <span>Find the point <strong>6 in / 15 cm</strong> below the armpit.</span></p>
    <p class="hint"><span class="hint-index">2</span> <span>Run the tape <strong>straight across the front</strong>, armpit to armpit.</span></p>
    <p class="hint"><span class="hint-index">3</span> <span>Keep it flat and level — don't pull it tight.</span></p>
  </div>

  <div class="panel mt-5 p-4">
    <div class="flex flex-wrap items-center justify-between gap-3">
      <label for="belt-measurement" class="font-semibold text-gray-800">Enter your measurement and we'll pick the size</label>
      <div class="flex items-center gap-2">
        <input id="belt-measurement" type="number" step="0.1" min="0" bind:value={measurement} on:input={applyMeasurement}
               placeholder="e.g. 18.2" class="w-28 rounded-lg border-gray-300 py-2 text-sm">
        <div class="unit-toggle">
          <button type="button" class="unit-button" class:active={measurementUnit === 'in'} on:click={() => setUnit('in')}>in</button>
          <button type="button" class="unit-button" class:active={measurementUnit === 'mm'} on:click={() => setUnit('mm')}>mm</button>
        </div>
      </div>
    </div>
    <p class="mt-3 text-sm text-gray-500">
      {measurement ? `Measurement applied — size ${selectedOption.label} selected.` : 'No measurement yet — you can also pick a size manually below.'}
    </p>
  </div>

  <div class="mt-6">
    <div class="section-label">
      <h3 class="font-semibold text-gray-900">
        {settings.productType === 'couple' ? 'Belt size for first account' : 'Belt size'}
      </h3>
      <span class="text-sm text-gray-500">Size {selectedOption.label} selected</span>
    </div>
    <div class="size-grid mt-3">
      {#each options as option, i}
        <button type="button" class="size-tile" class:selected={equipment === i + 1} on:click={() => selectSize(i)}>
          <span class="size-tile-label">{option.label}</span>
          <span class="size-tile-range">{option.rangeLabel}</span>
        </button>
      {/each}
    </div>
  </div>

  {#if settings.productType === 'couple'}
    <div class="mt-6">
      <div class="section-label">
        <h3 class="font-semibold text-gray-900">Belt size for second account</h3>
        <span class="text-sm text-gray-500">Size {options[equipment2 - 1].label} selected</span>
      </div>
      <div class="size-grid mt-3">
        {#each options as option, i}
          <button type="button" class="size-tile" class:selected={equipment2 === i + 1} on:click={() => selectSecondSize(i)}>
            <span class="size-tile-label">{option.label}</span>
            <span class="size-tile-range">{option.rangeLabel}</span>
          </button>
        {/each}
      </div>
    </div>
  {/if}

  {#if $location && $location.allow_local_pickup && !forcesLocalPickup($location, org)}
    <div class="mt-6">
      <div class="section-label">
        <h3 class="font-semibold text-gray-900">Delivery</h3>
        <span class="text-sm text-gray-500">Kit ships within 2 business days</span>
      </div>
      <div class="mt-3 flex flex-col gap-3 sm:flex-row">
        <label class="option-card" class:selected={settings.pick_up_location === 'Pick up at location'}>
          <input class="mt-1" type="radio" bind:group={settings.pick_up_location} value='Pick up at location'
                 style="accent-color: var(--secondary-color)">
          <span>
            <span class="block font-semibold text-gray-900">Pick up on site</span>
            <span class="block text-sm text-gray-500">Collect at your organization's front desk.</span>
          </span>
        </label>
        <label class="option-card" class:selected={settings.pick_up_location === 'Mail to me'}>
          <input class="mt-1" type="radio" bind:group={settings.pick_up_location} value='Mail to me'
                 style="accent-color: var(--secondary-color)">
          <span>
            <span class="block font-semibold text-gray-900">Mail it to me</span>
            <span class="block text-sm text-gray-500">Ships to your shipping address.</span>
          </span>
        </label>
      </div>
    </div>
  {/if}

  {#if addressOnFile && settings.pick_up_location === 'Mail to me'}
    <div class="panel mt-6 p-4">
      <div class="section-label">
        <h3 class="font-semibold text-gray-900">Shipping address</h3>
        <button type="button" class="text-sm font-semibold text-org-secondary hover:underline" on:click={useDifferentAddress}>
          Use a different address
        </button>
      </div>
      <p class="mt-2 text-sm text-gray-600">
        {addressOnFile.street}<br>
        {addressOnFile.city}{addressOnFile.state ? `, ${addressOnFile.state}` : ''} {addressOnFile.postal_code}<br>
        {addressOnFile.country || ''}
      </p>
    </div>
  {/if}

  {#if needsShipping}
    <div class="panel mt-6 p-4">
      <div class="section-label">
        <h3 class="font-semibold text-gray-900">Shipping address</h3>
        <span class="text-sm text-gray-500">We don't have one on file yet</span>
      </div>
      <div class="mt-3">
        <Billing bind:error={addressError} bind:customerData={billingAddress}/>
      </div>
    </div>
  {/if}

  {#if missingFields.length > 0}
    <div class="warning">
      <i class="fa-solid fa-exclamation-triangle"></i>
      <span>Missing shipping information: {missingFields.join(', ')}</span>
    </div>
  {/if}

  <button type="button" class="action-button" disabled={loading} on:click={() => {showWarning = true}}>
    {loading ? "Building your kit..." : "Select size and continue"}
  </button>
</div>

{#if zoomChart}
<div class="fixed inset-0 z-50 flex items-center justify-center bg-black/80" in:fade={{ duration: 150 }} out:fade={{ duration: 150 }} role="dialog" aria-modal="true">
  <div class="relative max-w-5xl w-[92vw] md:w-[80vw] bg-white rounded shadow-lg">
    <button
      class="absolute top-3 right-3 inline-flex h-8 w-8 items-center justify-center rounded-full bg-black text-white hover:bg-gray-800"
      on:click={() => (zoomChart = false)}
      aria-label="Close zoomed chart"
    >
      <i class="fa-solid fa-x"></i>
    </button>
    <img src="{BeltSize}" alt="enlarged size chart" class="w-full h-auto block" />
  </div>
  <button class="absolute inset-0 w-full h-full" on:click={() => (zoomChart = false)} aria-label="Dismiss"></button>
</div>
{/if}
<ConfirmationModal bind:this={modal} on:confirm={onSendSize} type="confirm" on:close={handleCloseWarning}
                   show={showWarning} title="Confirm Selection" actionButtonLabel="Confirm" dismissButtonLabel="Change Size" forceHalf={context != "APP"}>
    <svelte:fragment slot="message">
        <p class="text-black">By continuing, you are locking in your belt size for your order. Please confirm the size is correct.</p>
    </svelte:fragment>
</ConfirmationModal>
